import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { useTheme } from '../context/ThemeContext';

const Header = ({ setIsMobileOpen }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  
  const { isDarkMode, toggleTheme } = useTheme();

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [userRole, setUserRole] = useState('user'); 
  const [userData, setUserData] = useState({ 
    id: '', 
    name: 'Loading...', 
    email: '', 
    role: 'Loading...', 
    pic: null 
  });
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // --- 1. Decode JWT for Instant Role Checking (FIXED) ---
    const token = localStorage.getItem('capstonne_access_token');
    if (token) {
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const base64Url = parts[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64).split('').map(function(c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join('')
          );
          
          const payload = JSON.parse(jsonPayload);
          
          // 🚨 THE FIX: Check for 'privilege' exactly like we did in App & Sidebar!
          const role = payload.privilege || payload.role || 'user';
          setUserRole(role.toLowerCase());
        }
      } catch (e) {
        console.error("Failed to decode token for role in Header");
      }
    }

    // --- 2. Click Outside Listener ---
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    
    // --- 3. Fetch User Profile Data ---
    const fetchHeaderData = async () => {
      try {
        // If it's an admin, hit the admin profile endpoint instead of user profile
        const endpoint = userRole === 'admin' ? '/admin/profile' : '/profile/get';
        const profileRes = await axiosInstance.get(endpoint);
        
        if (profileRes.data) {
          const pData = profileRes.data;
          const userId = pData.id || "";
          
          // Only attempt to load image for regular users to prevent 404s
          const imageUrl = (userId && userRole !== 'admin')
            ? `${axiosInstance.defaults.baseURL}/profile/get-image/${userId}?t=${Date.now()}` 
            : null;

          setUserData({
            id: userId,
            name: pData.name || (userRole === 'admin' ? 'Main Admin' : 'Unknown User'),
            email: pData.email || "",
            role: pData.membership || (userRole === 'admin' ? 'System Admin' : 'Prospect'), 
            pic: imageUrl
          });
          setImageError(false);
        }
      } catch (error) {
        console.error("Failed to fetch header data", error);
      }
    };

    fetchHeaderData();
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userRole]); // Dependency added so it refetches correctly if role resolves

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login'; 
  };

  const getInitials = (name) => {
    if (!name || name === 'Loading...') return '??';
    const names = name.split(' ');
    if (names.length >= 2) return (names[0][0] + names[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <header className="
      h-20 sticky top-0 z-[100] flex-shrink-0
      flex items-center justify-between
      px-4 sm:px-6 lg:px-10
      bg-white dark:bg-slate-800
      border-b border-slate-200 dark:border-slate-700
      shadow-sm transition-colors duration-300
    ">
      
      {/* Left: Mobile Hamburger */}
      <div className="flex items-center">
        <button 
          onClick={() => setIsMobileOpen(true)}
          className="lg:hidden p-2 rounded-xl
            text-slate-500 dark:text-slate-400
            hover:text-blue-600 dark:hover:text-blue-400
            hover:bg-slate-100 dark:hover:bg-slate-700
            transition-colors focus:outline-none"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6h16M4 12h8m-8 6h16" />
          </svg>
        </button>
      </div>

      {/* Right Actions */}
      <div className="flex items-center space-x-2 sm:space-x-4">

        {/* Theme Toggle */}
        <button 
          onClick={toggleTheme}
          className="p-2.5 rounded-full
            bg-slate-100 dark:bg-slate-700
            text-amber-500 dark:text-yellow-400
            border border-slate-200 dark:border-slate-600
            hover:scale-110 active:scale-95
            transition-all shadow-sm"
          aria-label="Toggle Theme"
        >
          {isDarkMode ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
            </svg>
          )}
        </button>

        {/* Notification Bell */}
        <button className="relative p-2.5 rounded-full
          text-slate-400 dark:text-slate-400
          hover:text-blue-600 dark:hover:text-blue-400
          hover:bg-slate-100 dark:hover:bg-slate-700
          transition-all"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <span className="absolute top-2 right-2 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border-2 border-white dark:border-slate-800"></span>
          </span>
        </button>

        {/* Divider */}
        <div className="hidden sm:block h-7 w-px bg-slate-200 dark:bg-slate-700"></div>

        {/* User Profile Dropdown */}
        <div className="relative z-[60]" ref={dropdownRef}>
          <button 
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="flex items-center gap-2.5 focus:outline-none p-1 pr-2 rounded-full
              border-2 border-transparent
              hover:border-slate-200 dark:hover:border-slate-600
              hover:bg-slate-50 dark:hover:bg-slate-700/60
              transition-all group"
          >
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              {userData.pic && !imageError ? (
                <img 
                  src={userData.pic} 
                  alt="Profile" 
                  onError={() => setImageError(true)} 
                  className="w-9 h-9 rounded-full object-cover border-2 border-white dark:border-white shadow-sm" 
                />
              ) : (
                <div className="w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-sm
                bg-blue-50 dark:bg-slate-700
                text-blue-700 dark:text-blue-400
                border-2 border-white dark:border-white shadow-sm"
                >
                  {getInitials(userData.name)}
                </div>
              )}
              {/* Online dot */}
              <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white dark:border-slate-800 rounded-full"></div>
            </div>

            {/* Name + Role */}
            <div className="hidden md:flex flex-col items-start text-left max-w-[120px]">
              <span className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight truncate w-full">
                {userData.name}
              </span>
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 truncate w-full capitalize">
                {userData.role}
              </span>
            </div>

            {/* Chevron */}
            <svg
              className={`hidden md:block w-4 h-4 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Options */}
          {isProfileOpen && (
            <div className="absolute right-0 mt-3 w-64
              bg-white dark:bg-slate-800
              rounded-2xl shadow-xl
              border border-slate-100 dark:border-slate-700
              py-2 overflow-hidden
              animate-in fade-in slide-in-from-top-4 duration-200 z-50"
            >
              {/* User info header */}
              <div className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-700 mb-1">
                <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">{userData.name}</p>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 truncate mt-0.5">{userData.email || 'No email provided'}</p>
              </div>

              <div className="px-2 space-y-0.5">
                
                {/* --- DYNAMIC PROFILE LINK --- */}
                <Link
                  to={userRole === 'admin' ? "/dashboard/admin/profile" : "/dashboard/profile"}
                  onClick={() => setIsProfileOpen(false)}
                  className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold
                    text-slate-700 dark:text-slate-300
                    hover:bg-slate-100 dark:hover:bg-slate-700
                    hover:text-slate-900 dark:hover:text-white
                    transition-all"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                    bg-slate-100 dark:bg-slate-700
                    group-hover:bg-white dark:group-hover:bg-slate-600
                    shadow-sm transition-colors"
                  >
                    <svg className="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  {userRole === 'admin' ? 'Admin Profile' : 'My Profile'}
                </Link>

                {/* --- KYC LINK (ONLY SHOW TO USERS) --- */}
                {userRole === 'user' && (
                  <Link
                    to="/dashboard/kyc"
                    onClick={() => setIsProfileOpen(false)}
                    className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold
                      text-slate-700 dark:text-slate-300
                      hover:bg-slate-100 dark:hover:bg-slate-700
                      hover:text-slate-900 dark:hover:text-white
                      transition-all"
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                      bg-slate-100 dark:bg-slate-700
                      group-hover:bg-white dark:group-hover:bg-slate-600
                      shadow-sm transition-colors"
                    >
                      <svg className="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    Verify KYC
                  </Link>
                )}

              </div>

              <div className="h-px bg-slate-100 dark:bg-slate-700 my-2 mx-3"></div>

              {/* Sign Out */}
              <div className="px-2">
                <button
                  onClick={handleLogout}
                  className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-left
                    text-red-600 dark:text-red-400
                    hover:bg-red-50 dark:hover:bg-red-500/10
                    transition-all"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                    bg-red-50 dark:bg-slate-700
                    group-hover:bg-white dark:group-hover:bg-slate-600
                    shadow-sm transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </div>
                  Sign Out
                </button>
              </div>

            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;